import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Button } from './ui/button';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Textarea } from './ui/textarea';
import { Plus, Trash2, ChevronDown, Save, MapPin, DollarSign, Edit } from 'lucide-react';
import api from '../utils/api';
import { formatCurrency } from '../utils/helpers';
import { toast } from 'sonner';
import { useConfirm } from '../context/ConfirmContext';

export const SitesManager = forwardRef(({ customerId, customerBillingEmail, contacts = [], onContactsChanged }, ref) => {
  const confirm = useConfirm();
  const [sites, setSites] = useState([]);
  const [items, setItems] = useState([]);
  const [expandedSite, setExpandedSite] = useState(null);
  const [editingSite, setEditingSite] = useState(null);
  const [sitePrices, setSitePrices] = useState({});
  const [newPriceLine, setNewPriceLine] = useState({});
  const [loading, setLoading] = useState(true);

  const emptySite = {
    site_name: '',
    reference_number: '',
    status: 'ACTIVE',
    address_street: '',
    address_zip: '',
    address_city: '',
    address_canton: '',
    billing_email: customerBillingEmail || '',
    manager_first_name: '',
    manager_last_name: '',
    manager_phone: '',
    manager_email: '',
    notes: ''
  };

  useEffect(() => {
    loadSites();
    loadItems();
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadSites = async () => {
    try {
      const response = await api.getCustomerSites(customerId);
      setSites(response.data);
    } catch (error) {
      console.error('Failed to load sites:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async () => {
    try {
      const response = await api.getItems(true);
      setItems(response.data);
    } catch (error) {
      console.error('Failed to load items:', error);
    }
  };

  const loadSitePrices = async (siteId) => {
    try {
      const response = await api.getSitePrices(siteId);
      setSitePrices(prev => ({ ...prev, [siteId]: response.data }));
    } catch (error) {
      console.error('Failed to load site prices:', error);
    }
  };

  const toggleExpand = (siteId) => {
    if (expandedSite === siteId) {
      setExpandedSite(null);
    } else {
      setExpandedSite(siteId);
      if (!sitePrices[siteId]) {
        loadSitePrices(siteId);
      }
    }
  };

  const handleAddSite = () => {
    setEditingSite({ ...emptySite, isNew: true });
  };

  // Expose handleAddSite to parent via ref
  useImperativeHandle(ref, () => ({
    addSite: handleAddSite
  }));

  const handleEditSite = (site) => {
    setEditingSite({ ...site });
    setExpandedSite(site.id);
  };

  const handleSaveSite = async () => {
    if (!editingSite.site_name) {
      toast.error('Il nome del progetto è obbligatorio');
      return;
    }

    try {
      // Convert empty strings to null for email fields to pass Pydantic validation
      const cleanSiteData = (data) => {
        const cleaned = { ...data };
        if (cleaned.billing_email === '') cleaned.billing_email = null;
        if (cleaned.manager_email === '') cleaned.manager_email = null;
        if (cleaned.reference_number === '') cleaned.reference_number = null;
        return cleaned;
      };

      if (editingSite.isNew) {
        const { isNew, ...siteData } = editingSite;
        const response = await api.createSite(customerId, cleanSiteData(siteData));
        setSites([...sites, response.data]);
        toast.success('Progetto creato');
      } else {
        const { id, customer_id, created_at, ...siteData } = editingSite;
        await api.updateSite(editingSite.id, cleanSiteData(siteData));
        setSites(sites.map(s => s.id === editingSite.id ? { ...s, ...siteData } : s));
        toast.success('Progetto aggiornato');
      }
      setEditingSite(null);
      if (onContactsChanged) onContactsChanged();
    } catch (error) {
      toast.error('Errore nel salvataggio');
    }
  };

  const handleDeleteSite = async (siteId) => {
    const ok = await confirm({
      title: 'Elimina progetto',
      description: 'Stai per eliminare questo progetto. Tutti i prezzi specifici associati saranno persi.',
      confirmText: 'Elimina',
      destructive: true,
    });
    if (!ok) return;
    
    try {
      await api.deleteSite(siteId);
      setSites(sites.filter(s => s.id !== siteId));
      toast.success('Progetto eliminato');
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const handleAddPrice = async (siteId) => {
    const priceData = newPriceLine[siteId];
    if (!priceData?.item_id || !priceData?.unit_price_chf) {
      toast.error('Seleziona articolo e prezzo');
      return;
    }

    try {
      const response = await api.createSitePrice(siteId, {
        item_id: priceData.item_id,
        unit_price_chf: priceData.unit_price_chf,
        notes: priceData.notes || null
      });
      const existingPrices = sitePrices[siteId] || [];
      const existingIndex = existingPrices.findIndex(p => p.item_id === priceData.item_id);
      
      if (existingIndex >= 0) {
        existingPrices[existingIndex] = response.data;
        setSitePrices(prev => ({ ...prev, [siteId]: [...existingPrices] }));
      } else {
        setSitePrices(prev => ({ ...prev, [siteId]: [...existingPrices, response.data] }));
      }
      
      setNewPriceLine(prev => ({ ...prev, [siteId]: { item_id: '', unit_price_chf: '', notes: '' } }));
      toast.success('Prezzo salvato');
    } catch (error) {
      toast.error('Errore nel salvataggio prezzo');
    }
  };

  const handleDeletePrice = async (siteId, priceId) => {
    try {
      await api.deleteSitePrice(siteId, priceId);
      setSitePrices(prev => ({
        ...prev,
        [siteId]: prev[siteId].filter(p => p.id !== priceId)
      }));
      toast.success('Prezzo eliminato');
    } catch (error) {
      toast.error('Errore nell\'eliminazione');
    }
  };

  const getItemName = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item ? `${item.name} (${item.unit})` : itemId;
  };

  const getItemStandardPrice = (itemId) => {
    const item = items.find(i => i.id === itemId);
    return item?.standard_price_chf;
  };

  const getStatusLabel = (status) => {
    const labels = {
      'ACTIVE': 'Attivo',
      'SUSPENDED': 'Sospeso',
      'COMPLETED': 'Terminato'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status) => {
    const colors = {
      'ACTIVE': 'bg-green-100 text-green-700',
      'SUSPENDED': 'bg-yellow-100 text-yellow-700',
      'COMPLETED': 'bg-slate-100 text-slate-600'
    };
    return colors[status] || 'bg-slate-100 text-slate-600';
  };

  if (loading) {
    return <div className="p-4">Caricamento progetti...</div>;
  }

  return (
    <div className="space-y-4">
      {/* New Site Form */}
      {editingSite?.isNew && (
        <Card className="border-primary">
          <CardHeader>
            <CardTitle className="text-lg">Nuovo Progetto</CardTitle>
          </CardHeader>
          <CardContent>
            <SiteForm 
              site={editingSite} 
              setSite={setEditingSite} 
              onSave={handleSaveSite}
              onCancel={() => setEditingSite(null)}
              contacts={contacts}
            />
          </CardContent>
        </Card>
      )}

      {/* Sites List */}
      {sites.length === 0 && !editingSite?.isNew ? (
        <div className="text-center py-8 text-slate-500">
          <MapPin size={48} className="mx-auto mb-4 opacity-50" />
          <p>Nessun progetto associato a questo cliente</p>
        </div>
      ) : (
        <div className="space-y-3">
          {sites.map(site => {
            const isExpanded = expandedSite === site.id;
            const isEditing = editingSite?.id === site.id;

            return (
            <Card key={site.id} className={`overflow-hidden ${isExpanded ? 'border-primary' : ''}`}>
              <div 
                className="p-4 cursor-pointer hover:bg-secondary flex items-center justify-between"
                onClick={() => { if (isEditing) return; toggleExpand(site.id); }}
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-medium">{site.site_name}</span>
                    {site.reference_number && (
                      <span className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{site.reference_number}</span>
                    )}
                    <span className={`text-xs px-2 py-0.5 rounded ${getStatusColor(site.status)}`}>{getStatusLabel(site.status)}</span>
                  </div>
                  <div className="text-sm text-slate-500 mt-1">
                    {site.address_street && `${site.address_street}, `}
                    {site.address_zip} {site.address_city}
                    {site.manager_first_name && (
                      <span className="ml-3">Resp: {site.manager_first_name} {site.manager_last_name}</span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  {isExpanded && !isEditing && (
                    <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleEditSite(site); }} data-testid={`edit-site-${site.id}`}>
                      <Edit size={16} className="text-slate-600" />
                    </Button>
                  )}
                  {!isEditing && (
                    <ChevronDown size={20} className={`text-slate-400 transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                  )}
                </div>
              </div>

              {/* Expanded Content */}
              {isExpanded && (
                <div className="border-t">
                  {/* Edit Form */}
                  {isEditing && (
                    <div className="p-4 bg-slate-50">
                      <SiteForm 
                        site={editingSite} 
                        setSite={setEditingSite} 
                        onSave={handleSaveSite}
                        onCancel={() => setEditingSite(null)}
                        contacts={contacts}
                      />

                      {/* Editable prices section */}
                      <div className="mt-6 border-t pt-4">
                        <div className="flex items-center gap-2 mb-4">
                          <DollarSign size={18} className="text-slate-600" />
                          <h4 className="font-medium">Prezzi Specifici Progetto</h4>
                        </div>
                        {sitePrices[site.id]?.length > 0 && (
                          <div className="mb-4 space-y-2">
                            {sitePrices[site.id].map(price => (
                              <div key={price.id} className="p-3 bg-white rounded border">
                                <div className="flex items-center justify-between">
                                  <div className="flex-1">
                                    <span>{getItemName(price.item_id)}</span>
                                    {getItemStandardPrice(price.item_id) && getItemStandardPrice(price.item_id) !== price.unit_price_chf && (
                                      <span className="ml-3 text-sm text-slate-400 line-through">{formatCurrency(getItemStandardPrice(price.item_id))}</span>
                                    )}
                                  </div>
                                  <div className="flex items-center gap-4">
                                    <span className="font-mono font-semibold text-primary">{formatCurrency(price.unit_price_chf)}</span>
                                    <Button variant="ghost" size="sm" onClick={() => handleDeletePrice(site.id, price.id)} className="text-red-600 hover:text-red-700 h-8 w-8 p-0"><Trash2 size={14} /></Button>
                                  </div>
                                </div>
                                {price.notes && <p className="text-xs text-slate-500 mt-1 italic">{price.notes}</p>}
                              </div>
                            ))}
                          </div>
                        )}
                        {/* Add Price Row */}
                        <div className="space-y-3">
                          <div className="flex items-end gap-3">
                            <div className="flex-1">
                              <Label className="text-xs text-slate-500">Articolo</Label>
                              <Select value={newPriceLine[site.id]?.item_id || ''} onValueChange={(value) => setNewPriceLine(prev => ({ ...prev, [site.id]: { ...prev[site.id], item_id: value } }))}>
                                <SelectTrigger><SelectValue placeholder="Seleziona articolo..." /></SelectTrigger>
                                <SelectContent>
                                  {items.map(item => (
                                    <SelectItem key={item.id} value={item.id}><span>{item.name} ({item.unit})</span> <span className="text-xs text-slate-400 ml-2">Standard: {formatCurrency(item.standard_price_chf)}</span></SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            </div>
                            <div className="w-36">
                              <Label className="text-xs text-slate-500">Prezzo CHF</Label>
                              <Input type="number" step="0.01" placeholder="0.00" value={newPriceLine[site.id]?.unit_price_chf || ''} onChange={(e) => setNewPriceLine(prev => ({ ...prev, [site.id]: { ...prev[site.id], unit_price_chf: parseFloat(e.target.value) || '' } }))} />
                            </div>
                            <Button onClick={() => handleAddPrice(site.id)} size="sm"><Plus size={16} className="mr-1" /> Aggiungi</Button>
                          </div>
                          <div>
                            <Label className="text-xs text-slate-500">Note (opzionale)</Label>
                            <Input placeholder="es. Prezzo speciale accordato..." value={newPriceLine[site.id]?.notes || ''} onChange={(e) => setNewPriceLine(prev => ({ ...prev, [site.id]: { ...prev[site.id], notes: e.target.value } }))} />
                          </div>
                        </div>
                      </div>

                      {/* Delete site button */}
                      <div className="mt-6 pt-4 border-t flex justify-end">
                        <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700 hover:bg-red-50" onClick={() => { handleDeleteSite(site.id); setEditingSite(null); }} data-testid={`delete-site-${site.id}`}>
                          <Trash2 className="h-4 w-4 mr-1" /> Elimina progetto
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Read-only prices (when expanded but NOT editing) */}
                  {!isEditing && (
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <DollarSign size={16} className="text-slate-500" />
                        <h4 className="font-medium text-sm text-slate-700">Prezzi Specifici Progetto</h4>
                      </div>
                      {sitePrices[site.id]?.length > 0 ? (
                        <div className="space-y-1.5">
                          {sitePrices[site.id].map(price => (
                            <div key={price.id} className="flex items-center justify-between py-1.5 px-3 bg-slate-50 rounded">
                              <div>
                                <span className="text-sm">{getItemName(price.item_id)}</span>
                                {getItemStandardPrice(price.item_id) && getItemStandardPrice(price.item_id) !== price.unit_price_chf && (
                                  <span className="ml-2 text-xs text-slate-400 line-through">{formatCurrency(getItemStandardPrice(price.item_id))}</span>
                                )}
                              </div>
                              <span className="font-mono font-semibold text-primary">{formatCurrency(price.unit_price_chf)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-slate-400">Nessun prezzo specifico. Verranno usati i prezzi cliente o standard.</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </Card>
            );
          })}
        </div>
      )}
    </div>
  );
});

// Site Form Component
const SiteForm = ({ site, setSite, onSave, onCancel, contacts = [] }) => {
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const [contactSearch, setContactSearch] = useState('');

  const filteredContacts = contacts.filter(c => {
    if (!contactSearch) return true;
    const fullName = `${c.first_name} ${c.last_name}`.toLowerCase();
    return fullName.includes(contactSearch.toLowerCase());
  });

  const handleSelectContact = (contact) => {
    setSite({
      ...site,
      manager_contact_id: contact.id,
      manager_first_name: contact.first_name,
      manager_last_name: contact.last_name,
      manager_phone: contact.phone || '',
      manager_email: contact.email || ''
    });
    setShowContactDropdown(false);
    setContactSearch('');
  };

  const managerFullName = [site.manager_first_name, site.manager_last_name].filter(Boolean).join(' ');

  return (
    <div className="space-y-6">
      {/* Annulla + Salva in alto a destra */}
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={onCancel}>Annulla</Button>
        <Button size="sm" onClick={onSave} data-testid="save-site-btn"><Save size={16} className="mr-1" /> Salva</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="space-y-2 md:col-span-2">
          <Label>Nome Progetto *</Label>
          <Input value={site.site_name} onChange={(e) => setSite({ ...site, site_name: e.target.value })} placeholder="es. Ristrutturazione Villa Rossi" data-testid="site-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Stato</Label>
          <Select value={site.status || 'ACTIVE'} onValueChange={(value) => setSite({ ...site, status: value })}>
            <SelectTrigger data-testid="site-status-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="ACTIVE">Attivo</SelectItem>
              <SelectItem value="SUSPENDED">Sospeso</SelectItem>
              <SelectItem value="COMPLETED">Terminato</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2">
        <Label>Riferimento Fatturazione</Label>
        <Input value={site.reference_number} onChange={(e) => setSite({ ...site, reference_number: e.target.value })} placeholder="es. C-001" data-testid="site-reference-input" />
        <p className="text-xs text-slate-500">Usato per la fatturazione</p>
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-slate-700 mb-3">Indirizzo/Luogo</h4>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2 md:col-span-2">
            <Label>Via</Label>
            <Input value={site.address_street} onChange={(e) => setSite({ ...site, address_street: e.target.value })} placeholder="Via Roma 1" />
          </div>
          <div className="space-y-2">
            <Label>CAP</Label>
            <Input value={site.address_zip} onChange={(e) => setSite({ ...site, address_zip: e.target.value })} placeholder="6900" />
          </div>
          <div className="space-y-2">
            <Label>Citta</Label>
            <Input value={site.address_city} onChange={(e) => setSite({ ...site, address_city: e.target.value })} placeholder="Lugano" />
          </div>
          <div className="space-y-2">
            <Label>Cantone</Label>
            <Select value={site.address_canton} onValueChange={(value) => setSite({ ...site, address_canton: value })}>
              <SelectTrigger><SelectValue placeholder="Seleziona..." /></SelectTrigger>
              <SelectContent>
                <SelectItem value="TI">Ticino</SelectItem>
                <SelectItem value="GR">Grigioni</SelectItem>
                <SelectItem value="ZH">Zurigo</SelectItem>
                <SelectItem value="BE">Berna</SelectItem>
                <SelectItem value="LU">Lucerna</SelectItem>
                <SelectItem value="AG">Argovia</SelectItem>
                <SelectItem value="SG">San Gallo</SelectItem>
                <SelectItem value="VD">Vaud</SelectItem>
                <SelectItem value="GE">Ginevra</SelectItem>
                <SelectItem value="VS">Vallese</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-slate-700 mb-3">Email Fatturazione</h4>
        <div className="space-y-2">
          <Input type="email" value={site.billing_email} onChange={(e) => setSite({ ...site, billing_email: e.target.value })} placeholder="fatture@progetto.ch (default: email cliente)" />
          <p className="text-xs text-slate-500">Lascia vuoto per usare l'email del cliente</p>
        </div>
      </div>

      {/* Responsabile Progetto - inline fields with name dropdown */}
      <div className="border-t pt-4">
        <h4 className="text-sm font-medium text-slate-700 mb-3">Responsabile Progetto</h4>
        <p className="text-xs text-slate-400 mb-3">Opzionale. Se creato qui, verra aggiunto in automatico ai contatti del cliente.</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Nome + Cognome con dropdown contatti */}
          <div className="space-y-2 relative">
            <Label>Nome</Label>
            <div className="relative">
              <Input
                value={site.manager_first_name || ''}
                onChange={(e) => { setSite({ ...site, manager_first_name: e.target.value, manager_contact_id: null }); setContactSearch(e.target.value); }}
                onFocus={() => setShowContactDropdown(true)}
                placeholder="Nome responsabile"
                data-testid="manager-first-name"
              />
              <button
                type="button"
                onClick={() => setShowContactDropdown(!showContactDropdown)}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-slate-400 hover:text-slate-600"
                data-testid="manager-dropdown-toggle"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${showContactDropdown ? 'rotate-180' : ''}`} />
              </button>
            </div>
            {/* Dropdown contatti */}
            {showContactDropdown && contacts.length > 0 && (
              <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="contacts-dropdown">
                {filteredContacts.length === 0 ? (
                  <div className="px-3 py-2 text-xs text-slate-400">Nessun contatto trovato</div>
                ) : (
                  filteredContacts.map(c => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => handleSelectContact(c)}
                      className="w-full text-left px-3 py-2 hover:bg-secondary flex items-center justify-between text-sm border-b border-slate-50 last:border-0"
                    >
                      <span className="font-medium">{c.first_name} {c.last_name}</span>
                      {c.phone && <span className="text-xs text-slate-400">{c.phone}</span>}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>
          <div className="space-y-2">
            <Label>Cognome</Label>
            <Input value={site.manager_last_name || ''} onChange={(e) => setSite({ ...site, manager_last_name: e.target.value, manager_contact_id: null })} placeholder="Cognome responsabile" data-testid="manager-last-name" />
          </div>
          <div className="space-y-2">
            <Label>Telefono</Label>
            <Input value={site.manager_phone || ''} onChange={(e) => setSite({ ...site, manager_phone: e.target.value })} placeholder="+41 79 123 45 67" />
          </div>
          <div className="space-y-2">
            <Label>Email</Label>
            <Input type="email" value={site.manager_email || ''} onChange={(e) => setSite({ ...site, manager_email: e.target.value })} placeholder="email@esempio.ch" />
          </div>
        </div>
        {site.manager_contact_id && managerFullName && (
          <p className="text-xs text-blue-600 mt-2">Contatto esistente selezionato: {managerFullName}</p>
        )}
      </div>

      <div className="border-t pt-4">
        <Label>Note</Label>
        <Textarea value={site.notes} onChange={(e) => setSite({ ...site, notes: e.target.value })} placeholder="Note interne sul progetto..." rows={3} />
      </div>
    </div>
  );
};
